import React, { useMemo } from 'react';

import SalesChannelsEdit from '@components/shared/sales_channels/SalesChannelsEdit';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';

import { salesChannelService } from '@services/operator/salesChannelService';
import { shippingTypeService } from '@services/operator/shippingTypeService';

interface InventoryTypeOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface MerchantInventoryType {
  merchant_id: string;
  enabled?: boolean;
  inventory_type: InventoryTypeOption;
}

interface ShippingTypeOption {
  id: string;
  name?: string;
  code?: string;
  [key: string]: unknown;
}

// Operator 銷售通路編輯頁 wrapper — 與 Order / Inbound / ProductionBom 同一套
// canonical merchant-scope 修法（見 hooks/operator/useRecordMerchantSync.ts 開頭註解）。
//
// 編輯模式必須做的事：
//   1. 用 `useRecordMerchantSync()` 拿到 `handleRecordLoaded` 與 `merchantDataLoading`
//   2. 把 merchant-scoped reference data（inventoryTypes / shippingTypes）從 wrapper
//      注入 shared，shared 本身不再讀 OperatorDataContext
const OperatorSalesChannelsEdit: React.FC = () => {
  const { merchantInventoryTypes, merchantShippingTypes, currentMerchant } = useOperatorData() as {
    merchantInventoryTypes?: MerchantInventoryType[];
    merchantShippingTypes?: ShippingTypeOption[];
    currentMerchant: { id?: string } | null;
  };
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();

  // currentMerchant 在 edit 模式下會被 useRecordMerchantSync 切到 record 所屬 merchant。
  // Reference data 永遠跟著 currentMerchant 走，避免顯示其他 merchant 的選項。
  const effectiveMerchantId = currentMerchant?.id ?? null;

  const inventoryTypes = useMemo<InventoryTypeOption[]>(() => {
    if (!effectiveMerchantId) return [];
    return (merchantInventoryTypes ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type)
      .filter(Boolean);
  }, [merchantInventoryTypes, effectiveMerchantId]);

  // merchantShippingTypes 已經是 currentMerchant scope（OperatorDataContext 在 setCurrentMerchant
  // 時會 refresh），不需要再次 filter；但保留型別 narrowing。
  const shippingTypes = useMemo<ShippingTypeOption[]>(
    () => (merchantShippingTypes ?? []) as ShippingTypeOption[],
    [merchantShippingTypes]
  );

  return (
    <SalesChannelsEdit
      role="operator"
      salesChannelService={salesChannelService}
      shippingTypeService={shippingTypeService}
      inventoryTypes={inventoryTypes}
      shippingTypes={shippingTypes}
      merchantIdForCreate={effectiveMerchantId}
      onSalesChannelLoaded={handleRecordLoaded}
      merchantDataLoading={merchantDataLoading}
    />
  );
};

export default OperatorSalesChannelsEdit;
